// API Service for EHS Training Management
// SECURITY ENHANCED VERSION with JWT Authentication

// Get API URL from environment or use relative path for same-origin deployment
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

class ApiService {
  constructor() {
    // If API_URL is empty, use relative path (works when frontend & backend are on same domain)
    this.baseUrl = API_URL ? `${API_URL}/api` : '/api';
  }

  // ===================== TOKEN MANAGEMENT =====================
  
  getAccessToken() {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  setTokens(accessToken, refreshToken) {
    if (accessToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  clearTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  isTokenExpired(token) {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }

  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken || this.isTokenExpired(refreshToken)) {
      this.clearTokens();
      throw new Error('Session expirée. Veuillez vous reconnecter.');
    }

    try {
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${refreshToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        this.clearTokens();
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }

      const data = await response.json();
      this.setTokens(data.access_token, data.refresh_token);
      return data.access_token;
    } catch (error) {
      this.clearTokens();
      throw error;
    }
  }

  // ===================== REQUEST METHOD =====================

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Public routes that don't need authentication
    const publicRoutes = [
      '/auth/login',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/refresh',
      '/auth/invitation',
      '/auth/2fa/verify',  // 2FA verification during login
      '/verify/',
      '/qrcode/',
      '/health',
      '/settings/public'
    ];
    
    const isPublicRoute = publicRoutes.some(route => endpoint.startsWith(route));
    
    // Get access token
    let accessToken = this.getAccessToken();
    
    // Check if token needs refresh (for authenticated requests only)
    if (!isPublicRoute && accessToken && this.isTokenExpired(accessToken)) {
      try {
        accessToken = await this.refreshAccessToken();
      } catch (error) {
        // Token refresh failed, redirect to login
        window.dispatchEvent(new CustomEvent('auth:logout'));
        throw error;
      }
    }
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      // Add Authorization header if we have a token (not for public routes)
      if (!isPublicRoute && accessToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
      }
      
      // Add any additional headers
      if (options.headers) {
        Object.entries(options.headers).forEach(([key, value]) => {
          if (key.toLowerCase() !== 'content-type' && key.toLowerCase() !== 'authorization') {
            xhr.setRequestHeader(key, value);
          }
        });
      }
      
      xhr.onload = function() {
        let responseData;
        try {
          responseData = JSON.parse(xhr.responseText);
        } catch (e) {
          responseData = { detail: xhr.responseText || `Erreur ${xhr.status}` };
        }
        
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(responseData);
        } else if (xhr.status === 401) {
          // For public routes, show the actual error message
          if (isPublicRoute) {
            const errorMessage = responseData.detail || 'Erreur d\'authentification';
            reject(new Error(errorMessage));
          } else {
            // For authenticated routes, session expired
            window.dispatchEvent(new CustomEvent('auth:logout'));
            reject(new Error('Session expirée. Veuillez vous reconnecter.'));
          }
        } else if (xhr.status === 404) {
          // For public routes like reset-password verification, show the actual message
          const errorMessage = responseData.detail || 'Ressource non trouvée';
          reject(new Error(errorMessage));
        } else if (xhr.status === 429) {
          // Rate limited
          const retryAfter = responseData.detail || 'Trop de requêtes. Veuillez patienter.';
          reject(new Error(retryAfter));
        } else {
          const errorMessage = responseData.detail || responseData.message || `Erreur ${xhr.status}`;
          reject(new Error(errorMessage));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Erreur de connexion au serveur'));
      };
      
      if (options.body) {
        const body = typeof options.body === 'object' ? JSON.stringify(options.body) : options.body;
        xhr.send(body);
      } else {
        xhr.send();
      }
    });
  }

  // Generic HTTP methods
  async get(endpoint) {
    // Handle full paths like /api/competences
    const cleanEndpoint = endpoint.startsWith('/api') ? endpoint.replace('/api', '') : endpoint;
    return this.request(cleanEndpoint);
  }

  async post(endpoint, body) {
    const cleanEndpoint = endpoint.startsWith('/api') ? endpoint.replace('/api', '') : endpoint;
    return this.request(cleanEndpoint, { method: 'POST', body });
  }

  async put(endpoint, body) {
    const cleanEndpoint = endpoint.startsWith('/api') ? endpoint.replace('/api', '') : endpoint;
    return this.request(cleanEndpoint, { method: 'PUT', body });
  }

  async delete(endpoint) {
    const cleanEndpoint = endpoint.startsWith('/api') ? endpoint.replace('/api', '') : endpoint;
    return this.request(cleanEndpoint, { method: 'DELETE' });
  }

  // Health
  async healthCheck() {
    return this.request('/health');
  }

  // Notifications
  async getNotifications(lue = null) {
    const params = lue !== null ? `?lue=${lue}` : '';
    return this.request(`/notifications${params}`);
  }

  async markNotificationRead(notifId) {
    return this.request(`/notifications/${notifId}/read`, { method: 'POST' });
  }

  // Global Search
  async globalSearch(query, limit = 20) {
    if (!query || query.length < 2) {
      return { collaborateurs: [], autorisations: [], materiel: [], total: 0 };
    }
    return this.request(`/search?q=${encodeURIComponent(query)}&limit=${limit}`);
  }

  // Authentication
  async login(email, password) {
    const response = await this.request('/auth/login', { method: 'POST', body: { email, password } });
    
    // Check if 2FA is required
    if (response.requires_2fa) {
      return response; // Return without setting tokens - 2FA verification needed
    }
    
    // Store tokens if present
    if (response.access_token) {
      this.setTokens(response.access_token, response.refresh_token);
    }
    
    // Store CSRF token
    if (response.csrf_token) {
      localStorage.setItem('csrf_token', response.csrf_token);
    }
    
    return response;
  }

  async logout() {
    try {
      // Call backend logout to clear cookies
      await this.request('/auth/logout', { method: 'POST' });
    } catch (e) {
      // Continue even if logout fails
    }
    this.clearTokens();
    localStorage.removeItem('csrf_token');
    return { success: true };
  }

  // ===================== 2FA / TOTP METHODS =====================
  
  async verify2FA(userId, code) {
    const response = await this.request('/auth/2fa/verify', { 
      method: 'POST', 
      body: { user_id: userId, code } 
    });
    
    // Store tokens after successful 2FA verification
    if (response.access_token) {
      this.setTokens(response.access_token, response.refresh_token);
    }
    
    // Store CSRF token
    if (response.csrf_token) {
      localStorage.setItem('csrf_token', response.csrf_token);
    }
    
    return response;
  }

  async get2FAStatus() {
    return this.request('/auth/2fa/status');
  }

  async setup2FA(password) {
    return this.request('/auth/2fa/setup', { 
      method: 'POST', 
      body: { password } 
    });
  }

  async confirm2FASetup(code) {
    return this.request('/auth/2fa/confirm', { 
      method: 'POST', 
      body: { code } 
    });
  }

  async disable2FA(password) {
    return this.request('/auth/2fa/disable', { 
      method: 'POST', 
      body: { password } 
    });
  }

  // ===================== CSRF TOKEN =====================
  
  getCSRFToken() {
    return localStorage.getItem('csrf_token');
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async getInvitationByToken(token) {
    return this.request(`/auth/invitation/${token}`);
  }

  async acceptInvitation(token, password) {
    const response = await this.request('/auth/accept-invitation', { method: 'POST', body: { token, password } });
    
    // Store tokens if present
    if (response.access_token) {
      this.setTokens(response.access_token, response.refresh_token);
    }
    
    return response;
  }

  // Password Reset
  async forgotPassword(email) {
    // Pass current frontend URL for dynamic reset link
    const currentUrl = window.location.origin;
    return this.request('/auth/forgot-password', { 
      method: 'POST', 
      body: { email, base_url: currentUrl } 
    });
  }

  async verifyResetToken(token) {
    return this.request(`/auth/reset-password/${token}`);
  }

  async resetPassword(token, password) {
    return this.request('/auth/reset-password', { method: 'POST', body: { token, password } });
  }

  // QR Code & Verification
  async getDocumentQRCode(docType, docId) {
    // Pass current frontend URL for dynamic QR code generation
    const currentUrl = window.location.origin;
    return this.request(`/qrcode/${docType}/${docId}?base_url=${encodeURIComponent(currentUrl)}`);
  }

  async verifyDocument(docType, verificationCode) {
    return this.request(`/verify/${docType}/${verificationCode}`);
  }

  // Initialize default data
  async initData() {
    return this.request('/init-data', { method: 'POST' });
  }

  // Dashboard
  async getDashboardStats() {
    return this.request('/dashboard/stats');
  }

  async getUpcomingExpirations() {
    return this.request('/dashboard/expirations');
  }

  // Secteurs
  async getSecteurs() {
    return this.request('/secteurs');
  }

  async createSecteur(data) {
    return this.request('/secteurs', { method: 'POST', body: data });
  }

  async updateSecteur(id, data) {
    return this.request(`/secteurs/${id}`, { method: 'PUT', body: data });
  }

  async deleteSecteur(id) {
    return this.request(`/secteurs/${id}`, { method: 'DELETE' });
  }

  // Categories CACES
  async getCategoriesCACES() {
    return this.request('/categories-caces');
  }

  async createCategorieCACES(data) {
    return this.request('/categories-caces', { method: 'POST', body: data });
  }

  async updateCategorieCACES(id, data) {
    return this.request(`/categories-caces/${id}`, { method: 'PUT', body: data });
  }

  async deleteCategorieCACES(id) {
    return this.request(`/categories-caces/${id}`, { method: 'DELETE' });
  }

  // Types Visites
  async getTypesVisites() {
    return this.request('/types-visites');
  }

  async createTypeVisite(data) {
    return this.request('/types-visites', { method: 'POST', body: data });
  }

  async updateTypeVisite(id, data) {
    return this.request(`/types-visites/${id}`, { method: 'PUT', body: data });
  }

  async deleteTypeVisite(id) {
    return this.request(`/types-visites/${id}`, { method: 'DELETE' });
  }

  // Types Formations
  async getTypesFormations() {
    return this.request('/types-formations');
  }

  async createTypeFormation(data) {
    return this.request('/types-formations', { method: 'POST', body: data });
  }

  async updateTypeFormation(id, data) {
    return this.request(`/types-formations/${id}`, { method: 'PUT', body: data });
  }

  async deleteTypeFormation(id) {
    return this.request(`/types-formations/${id}`, { method: 'DELETE' });
  }

  // Types Matériel
  async getTypesMateriel() {
    return this.request('/types-materiel');
  }

  async createTypeMateriel(data) {
    return this.request('/types-materiel', { method: 'POST', body: data });
  }

  async updateTypeMateriel(id, data) {
    return this.request(`/types-materiel/${id}`, { method: 'PUT', body: data });
  }

  async deleteTypeMateriel(id) {
    return this.request(`/types-materiel/${id}`, { method: 'DELETE' });
  }

  // Checklist Sécurité Matériel
  async getChecklistElements() {
    return this.request('/checklist-elements');
  }

  async getChecklistEtats() {
    return this.request('/checklist-etats');
  }

  async createChecklistElement(data, userId, userNom) {
    return this.request(
      `/checklist-elements?user_id=${encodeURIComponent(userId)}&user_nom=${encodeURIComponent(userNom)}`,
      { method: 'POST', body: data }
    );
  }

  async updateChecklistElement(code, data, userId, userNom) {
    return this.request(
      `/checklist-elements/${code}?user_id=${encodeURIComponent(userId)}&user_nom=${encodeURIComponent(userNom)}`,
      { method: 'PUT', body: data }
    );
  }

  async deleteChecklistElement(code) {
    return this.request(`/checklist-elements/${code}`, { method: 'DELETE' });
  }

  async getTypeMaterielChecklist(typeId) {
    return this.request(`/types-materiel/${typeId}/checklist`);
  }

  async saveTypeMaterielChecklist(typeId, data, userId, userNom) {
    return this.request(
      `/types-materiel/${typeId}/checklist?user_id=${encodeURIComponent(userId)}&user_nom=${encodeURIComponent(userNom)}`,
      { method: 'POST', body: data }
    );
  }

  async getMaterielChecklist(materielId) {
    return this.request(`/materiel/${materielId}/checklist`);
  }

  async saveMaterielChecklist(materielId, data, userId, userNom, userRole) {
    return this.request(
      `/materiel/${materielId}/checklist?user_id=${encodeURIComponent(userId)}&user_nom=${encodeURIComponent(userNom)}&user_role=${encodeURIComponent(userRole)}`,
      { method: 'POST', body: data }
    );
  }

  async getMaterielChecklistHistorique(materielId) {
    return this.request(`/materiel/${materielId}/checklist/historique`);
  }

  // Upload d'image pour checklist
  async uploadChecklistImage(file, materielId, elementCode) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('materiel_id', materielId);
    formData.append('element_code', elementCode);
    
    const token = this.getAccessToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${this.baseUrl}/checklist/upload-image`, {
      method: 'POST',
      headers: headers,
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Erreur lors de l\'upload');
    }
    
    return response.json();
  }

  async deleteChecklistImage(filename) {
    return this.request(`/checklist/images/${filename}`, { method: 'DELETE' });
  }

  // Dashboard Matériel
  async getMaterielDashboardStats() {
    return this.request('/materiel/dashboard/stats');
  }

  async getMaterielVGPEcheances() {
    return this.request('/materiel/dashboard/ct-echeances');
  }

  // Alias pour compatibilité
  async getMaterielCTEcheances() {
    return this.getMaterielVGPEcheances();
  }

  async getMaterielNonConformites() {
    return this.request('/materiel/dashboard/non-conformites');
  }

  // Collaborateurs
  async getCollaborateurs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/collaborateurs${queryString ? `?${queryString}` : ''}`);
  }

  async getCollaborateur(id) {
    return this.request(`/collaborateurs/${id}`);
  }

  async createCollaborateur(data) {
    return this.request('/collaborateurs', { method: 'POST', body: data });
  }

  async updateCollaborateur(id, data) {
    return this.request(`/collaborateurs/${id}`, { method: 'PUT', body: data });
  }

  async deleteCollaborateur(id) {
    return this.request(`/collaborateurs/${id}`, { method: 'DELETE' });
  }

  // CACES
  async getAllCACES(userRole = '', includeArchives = false) {
    let params = [];
    if (userRole) params.push(`user_role=${encodeURIComponent(userRole)}`);
    if (includeArchives) params.push('include_archives=true');
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return this.request(`/caces${queryString}`);
  }

  async getCollaborateurCACES(collaborateurId) {
    return this.request(`/collaborateurs/${collaborateurId}/caces`);
  }

  async addCACES(collaborateurId, data) {
    return this.request(`/collaborateurs/${collaborateurId}/caces`, { method: 'POST', body: data });
  }

  async updateCACES(id, data) {
    return this.request(`/caces/${id}`, { method: 'PUT', body: data });
  }

  async deleteCACES(id) {
    return this.request(`/caces/${id}`, { method: 'DELETE' });
  }

  // Visites Medicales
  async getAllVisitesMedicales(userRole = '', includeArchives = false) {
    let params = [];
    if (userRole) params.push(`user_role=${encodeURIComponent(userRole)}`);
    if (includeArchives) params.push('include_archives=true');
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return this.request(`/visites-medicales${queryString}`);
  }

  async getCollaborateurVisites(collaborateurId) {
    return this.request(`/collaborateurs/${collaborateurId}/visites-medicales`);
  }

  async getCollaborateurAutorisations(collaborateurId) {
    return this.request(`/autorisations?collaborateur_id=${collaborateurId}`);
  }

  async addVisiteMedicale(collaborateurId, data) {
    return this.request(`/collaborateurs/${collaborateurId}/visites`, { method: 'POST', body: data });
  }

  async updateVisiteMedicale(id, data) {
    return this.request(`/visites-medicales/${id}`, { method: 'PUT', body: data });
  }

  async deleteVisiteMedicale(id) {
    return this.request(`/visites-medicales/${id}`, { method: 'DELETE' });
  }

  // Formations
  async getAllFormations(includeArchives = false) {
    const params = includeArchives ? '?include_archives=true' : '';
    return this.request(`/formations${params}`);
  }

  async addFormation(collaborateurId, data) {
    return this.request(`/collaborateurs/${collaborateurId}/formations`, { method: 'POST', body: data });
  }

  async createFormation(data) {
    return this.request('/formations', { method: 'POST', body: data });
  }

  async updateFormation(id, data) {
    return this.request(`/formations/${id}`, { method: 'PUT', body: data });
  }

  async deleteFormation(id) {
    return this.request(`/formations/${id}`, { method: 'DELETE' });
  }

  async generateFormationAttestation(id) {
    // Pass current frontend URL for dynamic QR code in attestation
    const currentUrl = window.location.origin;
    return this.request(`/formations/${id}/generate-attestation`, { 
      method: 'POST',
      body: { base_url: currentUrl }
    });
  }

  // Materiel
  async getMateriel(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/materiel${queryString ? `?${queryString}` : ''}`);
  }

  async createMateriel(data) {
    return this.request('/materiel', { method: 'POST', body: data });
  }

  async updateMateriel(id, data) {
    return this.request(`/materiel/${id}`, { method: 'PUT', body: data });
  }

  async deleteMateriel(id) {
    return this.request(`/materiel/${id}`, { method: 'DELETE' });
  }

  // Autorisations
  async getAutorisations(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/autorisations${queryString ? `?${queryString}` : ''}`);
  }

  async getAutorisation(id) {
    return this.request(`/autorisations/${id}`);
  }

  async getAutorisationQRCode(id) {
    // Pass current frontend URL for dynamic QR code generation
    const currentUrl = window.location.origin;
    return this.request(`/qrcode/autorisation/${id}?base_url=${encodeURIComponent(currentUrl)}`);
  }

  async generateAutorisationDocument(id) {
    // Pass current frontend URL for dynamic QR code in document
    const currentUrl = window.location.origin;
    return this.request(`/autorisations/${id}/generate-document`, { 
      method: 'POST',
      body: { base_url: currentUrl }
    });
  }

  async createAutorisation(data, userId, userNom) {
    const params = new URLSearchParams({ user_id: userId || 'system', user_nom: userNom || 'System' });
    return this.request(`/autorisations?${params}`, { method: 'POST', body: data });
  }

  async submitAutorisation(id) {
    return this.request(`/autorisations/${id}/soumettre`, { method: 'POST' });
  }

  async updateAutorisation(id, data) {
    return this.request(`/autorisations/${id}`, { method: 'PUT', body: data });
  }

  async validateAutorisationHSE(id, data) {
    return this.request(`/autorisations/${id}/validate-hse`, { method: 'POST', body: data });
  }

  async signAutorisation(id, data) {
    return this.request(`/autorisations/${id}/sign`, { method: 'POST', body: data });
  }

  async revokeAutorisation(id, data) {
    return this.request(`/autorisations/${id}/revoke`, { method: 'POST', body: data });
  }

  async rejectAutorisation(id, data) {
    return this.request(`/autorisations/${id}/reject`, { method: 'POST', body: data });
  }

  async closeAutorisation(id, userId, userNom) {
    return this.request(`/autorisations/${id}/close?user_id=${encodeURIComponent(userId)}&user_nom=${encodeURIComponent(userNom)}`, { method: 'POST' });
  }

  async deleteAutorisation(id) {
    return this.request(`/autorisations/${id}`, { method: 'DELETE' });
  }

  // Demandes (alias for workflow)
  async getDemandes(statut) {
    return this.request(`/demandes${statut ? `?statut=${statut}` : ''}`);
  }

  // Utilisateurs
  async getUtilisateurs(role) {
    return this.request(`/utilisateurs${role ? `?role=${role}` : ''}`);
  }

  async getUtilisateur(id) {
    return this.request(`/utilisateurs/${id}`);
  }

  async createUtilisateur(data) {
    return this.request('/utilisateurs', { method: 'POST', body: data });
  }

  async updateUtilisateur(id, data) {
    return this.request(`/utilisateurs/${id}`, { method: 'PUT', body: data });
  }

  async deleteUtilisateur(id) {
    // Note: Authorization is now handled via JWT token in the Authorization header
    return this.request(`/utilisateurs/${id}`, { method: 'DELETE' });
  }

  async updateUserSignature(id, signatureData) {
    return this.request(`/utilisateurs/${id}/signature`, { method: 'PUT', body: { signature_data: signatureData } });
  }

  // Profile Management
  async getProfile(userId) {
    return this.request(`/profile/${userId}`);
  }

  async updateProfile(userId, data) {
    return this.request(`/profile/${userId}`, { method: 'PUT', body: data });
  }

  async changePassword(userId, currentPassword, newPassword) {
    return this.request(`/profile/${userId}/change-password`, { 
      method: 'POST', 
      body: { current_password: currentPassword, new_password: newPassword } 
    });
  }

  async updateAvatar(userId, avatarId) {
    return this.request(`/profile/${userId}/avatar`, { 
      method: 'PUT', 
      body: { avatar: avatarId } 
    });
  }

  // Profile Photo Upload
  async uploadProfilePhoto(userId, file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = this.getAccessToken();
    const response = await fetch(`${this.baseUrl}/profile/${userId}/photo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Erreur lors de l\'upload de la photo');
    }
    
    return response.json();
  }

  async deleteProfilePhoto(userId) {
    return this.request(`/profile/${userId}/photo`, { method: 'DELETE' });
  }

  getProfilePhotoUrl(userId) {
    return `${this.baseUrl}/profile/${userId}/photo`;
  }

  // Roles
  async getRoles() {
    return this.request('/roles');
  }

  async createRole(data) {
    return this.request('/roles', { method: 'POST', body: data });
  }

  async updateRole(id, data) {
    return this.request(`/roles/${id}`, { method: 'PUT', body: data });
  }

  async deleteRole(id) {
    return this.request(`/roles/${id}`, { method: 'DELETE' });
  }

  // Invitations
  async getInvitations(statut) {
    return this.request(`/invitations${statut ? `?statut=${statut}` : ''}`);
  }

  async createInvitation(data, userId, userNom) {
    // Add current frontend URL for dynamic invitation link
    const currentUrl = window.location.origin;
    return this.request(`/invitations?user_id=${userId}&user_nom=${encodeURIComponent(userNom)}`, { 
      method: 'POST', 
      body: { ...data, base_url: currentUrl }
    });
  }

  async cancelInvitation(id) {
    return this.request(`/invitations/${id}/cancel`, { method: 'POST' });
  }

  async resendInvitation(id) {
    // Add current frontend URL for dynamic invitation link
    const currentUrl = window.location.origin;
    return this.request(`/invitations/${id}/resend`, { 
      method: 'POST',
      body: { base_url: currentUrl }
    });
  }

  // OTP
  async generateOTP(userId, purpose = 'signature') {
    return this.request('/otp/generate', { method: 'POST', body: { user_id: userId, purpose } });
  }

  async verifyOTP(userId, code, purpose = 'signature') {
    return this.request('/otp/verify', { method: 'POST', body: { user_id: userId, code, purpose } });
  }

  // Signatures
  async getSignatures(userId) {
    return this.request(`/signatures${userId ? `?user_id=${userId}` : ''}`);
  }

  async createSignature(data) {
    return this.request('/signatures', { method: 'POST', body: data });
  }

  // Modèles de Documents
  async getModelesDocuments(type, actif) {
    let params = [];
    if (type) params.push(`type=${type}`);
    if (actif !== undefined) params.push(`actif=${actif}`);
    return this.request(`/modeles-documents${params.length ? `?${params.join('&')}` : ''}`);
  }

  async getModeleDocument(id) {
    return this.request(`/modeles-documents/${id}`);
  }

  async createModeleDocument(data, userId, userNom) {
    return this.request(`/modeles-documents?user_id=${userId}&user_nom=${encodeURIComponent(userNom)}`, {
      method: 'POST',
      body: data
    });
  }

  async updateModeleDocument(id, data) {
    return this.request(`/modeles-documents/${id}`, { method: 'PUT', body: data });
  }

  async deleteModeleDocument(id) {
    return this.request(`/modeles-documents/${id}`, { method: 'DELETE' });
  }

  async previewModeleDocument(id, data) {
    return this.request(`/modeles-documents/${id}/preview`, { method: 'POST', body: data });
  }

  // Scheduler
  async getSchedulerStatus() {
    return this.request('/scheduler/status');
  }

  async triggerExpirationCheck() {
    return this.request('/scheduler/check-expirations', { method: 'POST' });
  }

  // Sessions de Formation (multi-personnes)
  async getSessionsFormations() {
    return this.request('/sessions-formations');
  }

  async getSessionFormation(id) {
    return this.request(`/sessions-formations/${id}`);
  }

  async createSessionFormation(data, userId, userNom) {
    return this.request(`/sessions-formations?user_id=${userId}&user_nom=${encodeURIComponent(userNom)}`, {
      method: 'POST',
      body: data
    });
  }

  async deleteSessionFormation(id) {
    return this.request(`/sessions-formations/${id}`, { method: 'DELETE' });
  }

  // Sessions CACES (multi-personnes)
  async getSessionsCACES() {
    return this.request('/sessions-caces');
  }

  async getSessionCACES(id) {
    return this.request(`/sessions-caces/${id}`);
  }

  async createSessionCACES(data, userId, userNom) {
    return this.request(`/sessions-caces?user_id=${userId}&user_nom=${encodeURIComponent(userNom)}`, {
      method: 'POST',
      body: data
    });
  }

  async deleteSessionCACES(id) {
    return this.request(`/sessions-caces/${id}`, { method: 'DELETE' });
  }

  // Planning de Formations
  async getPlanningFormations(statut, typeFormationId) {
    let params = [];
    if (statut) params.push(`statut=${statut}`);
    if (typeFormationId) params.push(`type_formation_id=${typeFormationId}`);
    return this.request(`/planning-formations${params.length ? `?${params.join('&')}` : ''}`);
  }

  async getPlanningFormation(id) {
    return this.request(`/planning-formations/${id}`);
  }

  async createPlanningFormation(data, userId, userNom, userEmail) {
    return this.request(`/planning-formations?user_id=${userId}&user_nom=${encodeURIComponent(userNom)}&user_email=${encodeURIComponent(userEmail)}`, {
      method: 'POST',
      body: data
    });
  }

  async updatePlanningFormation(id, data) {
    return this.request(`/planning-formations/${id}`, { method: 'PUT', body: data });
  }

  async deletePlanningFormation(id) {
    return this.request(`/planning-formations/${id}`, { method: 'DELETE' });
  }

  // Inscriptions aux Formations
  async getInscriptionsPlanning(planningId) {
    return this.request(`/planning-formations/${planningId}/inscriptions`);
  }

  async createInscription(planningId, data, userId, userNom) {
    return this.request(`/planning-formations/${planningId}/inscriptions?user_id=${userId}&user_nom=${encodeURIComponent(userNom)}`, {
      method: 'POST',
      body: data
    });
  }

  async createInscriptionCACES(planningId, data, userId, userNom) {
    return this.request(`/planning-caces/${planningId}/inscriptions?user_id=${userId}&user_nom=${encodeURIComponent(userNom)}`, {
      method: 'POST',
      body: data
    });
  }

  async updateInscriptionStatut(inscriptionId, statut) {
    return this.request(`/inscriptions/${inscriptionId}/statut?statut=${statut}`, { method: 'PUT' });
  }

  async deleteInscription(inscriptionId) {
    return this.request(`/inscriptions/${inscriptionId}`, { method: 'DELETE' });
  }

  async deleteInscriptionCACES(inscriptionId) {
    return this.request(`/inscriptions-caces/${inscriptionId}`, { method: 'DELETE' });
  }

  async getManagerTeamInscriptions(managerId) {
    return this.request(`/manager/${managerId}/inscriptions`);
  }

  // Planning CACES
  async getPlanningCACES(statut, categorieId) {
    const params = [];
    if (statut) params.push(`statut=${statut}`);
    if (categorieId) params.push(`categorie_id=${categorieId}`);
    return this.request(`/planning-caces${params.length ? `?${params.join('&')}` : ''}`);
  }

  async getPlanningCACESDetail(id) {
    return this.request(`/planning-caces/${id}`);
  }

  async createPlanningCACES(data, userId, userNom, userEmail) {
    return this.request(`/planning-caces?user_id=${userId}&user_nom=${encodeURIComponent(userNom)}&user_email=${encodeURIComponent(userEmail)}`, {
      method: 'POST',
      body: data
    });
  }

  async deletePlanningCACES(id) {
    return this.request(`/planning-caces/${id}`, { method: 'DELETE' });
  }

  // Documents
  async uploadDocument(file, entityType, entityId, description, userId, userNom) {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', entityType);
      formData.append('entity_id', entityId);
      formData.append('description', description);
      formData.append('user_id', userId);
      formData.append('user_nom', userNom);
      
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.baseUrl}/documents/upload`, true);
      
      // Add Authorization header
      const token = this.getAccessToken();
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } catch (e) {
            reject(new Error('Réponse invalide du serveur'));
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText);
            reject(new Error(error.detail || `Erreur ${xhr.status}`));
          } catch (e) {
            reject(new Error(`Erreur serveur: ${xhr.status}`));
          }
        }
      };
      
      xhr.onerror = () => {
        reject(new Error('Erreur de connexion au serveur'));
      };
      
      xhr.send(formData);
    });
  }

  async getDocuments(entityType, entityId) {
    return this.request(`/documents/${entityType}/${entityId}`);
  }

  async downloadDocument(docId, userRole) {
    const token = this.getAccessToken();
    const response = await fetch(`${this.baseUrl}/documents/download/${docId}?user_role=${encodeURIComponent(userRole)}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      let errorMessage = 'Erreur lors du téléchargement';
      try {
        const error = await response.json();
        errorMessage = error.detail || errorMessage;
      } catch (e) {
        // Response is not JSON
      }
      throw new Error(errorMessage);
    }
    
    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'document';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, '');
      }
    }
    
    // Download the file
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  async viewDocument(docId, userRole) {
    const token = this.getAccessToken();
    const response = await fetch(`${this.baseUrl}/documents/view/${docId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      let errorMessage = 'Erreur lors de la visualisation';
      try {
        const error = await response.json();
        errorMessage = error.detail || errorMessage;
      } catch (e) {
        // Response is not JSON
      }
      throw new Error(errorMessage);
    }
    
    // Open in new tab
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
  }

  async deleteDocument(docId, userRole) {
    return this.request(`/documents/${docId}?user_role=${encodeURIComponent(userRole)}`, { method: 'DELETE' });
  }

  // ===================== RGPD / GDPR =====================

  // Get all personal data (RGPD Article 15 - Right of Access)
  async getMyPersonalData() {
    return this.request('/rgpd/my-data');
  }

  // Export personal data as file (RGPD Article 20 - Right to Portability)
  async exportPersonalData() {
    const token = this.getAccessToken();
    const response = await fetch(`${this.baseUrl}/rgpd/export`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      throw new Error('Erreur lors de l\'export des données');
    }
    return response.blob();
  }

  // Request data deletion (RGPD Article 17 - Right to Erasure)
  async requestDataDeletion(confirmation, password, reason = '') {
    return this.request('/rgpd/deletion-request', {
      method: 'POST',
      body: { confirmation, password, reason }
    });
  }

  // Get deletion request status
  async getDeletionRequestStatus() {
    return this.request('/rgpd/deletion-status');
  }

  // Request data rectification (RGPD Article 16)
  async requestDataRectification(corrections, justification = '') {
    return this.request('/rgpd/rectification', {
      method: 'POST',
      body: { corrections, justification }
    });
  }

  // Record consent
  async recordConsent(consentType, consents, version = '1.0') {
    return this.request('/rgpd/consent', {
      method: 'POST',
      body: { type: consentType, consents, version }
    });
  }

  // Get consent log
  async getConsentLog() {
    return this.request('/rgpd/consent-log');
  }

  // ===================== SYSTEM SETTINGS =====================

  // Get system settings (Admin gets full settings, others get public info)
  async getSystemSettings() {
    return this.request('/settings/system');
  }

  // Get public settings (no auth required)
  async getPublicSettings() {
    return this.request('/settings/public');
  }

  // Update system settings (Admin only)
  async updateSystemSettings(settings) {
    return this.request('/settings/system', {
      method: 'PUT',
      body: settings
    });
  }
}

export const api = new ApiService();
export default api;
